import { ReactElement, useEffect, useState } from "react";

import { useOrganization } from "@asd/customer-portal-ui";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  Stack,
  SvgIconProps,
  SvgIconPropsColorOverrides,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { useKeycloak } from "~/components/authorization";
import ProviderRegistration from "./ProviderRegistration";
import SsnForm from "./SsnForm";

interface ROLE {
  id: string;
  label: string;
  description: string;
  icon: ReactElement<SvgIconProps>;
  features: string[];
  color: keyof SvgIconPropsColorOverrides;
}

const ROLES = [
  {
    id: "participant",
    label: "Participant/Member",
    description: "I receive services as a program participant/member",
    icon: <PersonOutlineIcon fontSize="large" />,
    features: ["Manager Services", "Track Usage", "24/7 Access"],
    color: "primary",
  },
  {
    id: "provider",
    label: "Service Provider",
    description: "I provide services as a direct care professional or vendor",
    icon: <MedicalServicesOutlinedIcon fontSize="large" />,
    features: ["Offer Services", "Share Preferences", "Grow Your Business"],
    color: "primary",
  },
] as ROLE[];

type UserTypes = (typeof ROLES)[number]["id"];

const CompleteRegistration = () => {
  const keycloak = useKeycloak();

  const {
    configuration: { supportCoordinator },
  } = useOrganization();

  const navigate = useNavigate();

  const [userType, setUserType] = useState<null | UserTypes>(null);
  const [isPending, setIsPending] = useState(true);
  const [showProviderConfirmation, setShowProviderConfirmation] = useState(false);

  const handleSelectionClick = () => {
    if (userType === "participant") {
      setIsPending(false);
    } else {
      setShowProviderConfirmation(true);
    }
  };

  const handleProviderConfirmationResponse =
    (confirmed: boolean = false) =>
    () => {
      setShowProviderConfirmation(false);

      if (confirmed) {
        setIsPending(false);
      }
    };

  useEffect(() => {
    if (Object.keys(keycloak).length && !keycloak.onVerificationCallback) {
      navigate("/", { replace: true });
    }
  }, [keycloak, navigate]);

  if (!Object.keys(keycloak).length) {
    return (
      <CircularProgress
        size={150}
        sx={{ position: "absolute", justifySelf: "anchor-center", alignSelf: "anchor-center", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      />
    );
  }

  return !keycloak.onVerificationCallback ? null : (
    <>
      {isPending && (
        <Stack alignSelf="center" direction="column" flexGrow={1} justifyContent="center" maxWidth="md" padding={2}>
          <Card elevation={6} sx={{ padding: 4, width: "100%", minWidth: (theme) => theme.breakpoints.values.sm / 2 }}>
            <Typography align="center" color="custom.main" fontWeight="bold" mb={4} variant="h4" gutterBottom>
              Choose Your Role
            </Typography>
            <Typography align="center" mb={2} gutterBottom>
              Are you a Participant/Member or Service Provider?
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} mb={4} spacing={2}>
              {ROLES.map((role) => {
                const isSelected = userType === role.id;

                return (
                  <Card
                    key={role.id}
                    sx={{
                      flex: 1,
                      borderWidth: 2,
                      borderColor: isSelected ? `${role.color}.main` : "divider",
                      borderRadius: 2,
                      transition: "border-color 0.2s, box-shadow 0.2s",
                      boxShadow: isSelected ? 3 : 0,
                    }}
                    variant="outlined"
                  >
                    <CardActionArea onClick={() => setUserType(role.id)} sx={{ height: "100%", alignItems: "flex-start" }}>
                      <CardContent sx={{ display: "flex", flexDirection: "column", p: 3, height: "100%" }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 2,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: isSelected ? `${role.color}.main` : `${role.color}.50`,
                              color: isSelected ? "white" : `${role.color}.main`,
                              width: 52,
                              height: 52,
                              transition: "background-color 0.2s",
                            }}
                          >
                            {role.icon}
                          </Avatar>
                          {isSelected && <CheckCircleIcon color={role.color} />}
                        </Box>
                        <Typography fontWeight={600} variant="h6" gutterBottom>
                          {role.label}
                        </Typography>
                        <Typography color="text.secondary" mb={2} variant="body2">
                          {role.description}
                        </Typography>
                        <Box flexGrow={1} />
                        <Stack direction="row" flexWrap="wrap" gap={0.75}>
                          {role.features.map((f) => (
                            <Chip
                              key={f}
                              // @ts-ignore
                              color={isSelected ? role.color : "default"}
                              label={f}
                              size="small"
                              variant={isSelected ? "filled" : "outlined"}
                            />
                          ))}
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Stack>
            <Button disabled={!userType} onClick={handleSelectionClick} sx={{ mt: 3, mb: 2 }} fullWidth>
              {!userType ? "Select a Role" : `Continue as ${ROLES.find(({ id }) => userType === id)?.label}`}
            </Button>
          </Card>
        </Stack>
      )}
      <Dialog maxWidth="md" onClose={handleProviderConfirmationResponse()} open={showProviderConfirmation} fullWidth>
        <DialogTitle gap={2}>
          <Stack alignItems="baseline" direction="row" gap={2}>
            <Icon color="warning" fontSize="large">
              <WarningIcon fontSize="large" />
            </Icon>
            <Typography variant="h4">Confirm Service Provider Role</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            The Service Provider account is meant only to list services on the Provider Directory.
            <br />
            <br />
            If you are trying to view a participant/member&apos;s services, please click &quot;Cancel&quot; and select the
            &quot;Participant/Member&quot; role.
            <br />
            If you are a {supportCoordinator.title}, please return to the registration page and restart your registration.
            <br />
            <br />
            Proceeding with this account will not allow you to view any participant/member information.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProviderConfirmationResponse()} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleProviderConfirmationResponse(true)}>Continue as Service Provider</Button>
        </DialogActions>
      </Dialog>
      {!isPending && userType === "participant" && <SsnForm />}
      {!isPending && userType === "provider" && <ProviderRegistration />}
    </>
  );
};

export default CompleteRegistration;
